import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { MenuIcon } from '@/components/MenuIcon';
import type { PartyType, VendorKind } from '@/lib/auth/session';
import { hrefOf, menuFor, RFQ_SPACE, spaceOfPath, type MenuGroup, type MenuItem } from '@/lib/menu';
import { useTodoCounts } from '@/lib/useTodoCounts';

/*
  좌측 메뉴 — front 의 `Navigation` 과 같은 방식이다.

  · 골격은 `lib/menu.ts` 한 곳에서 온다. 여기는 그리기만 한다.
  · 묶음은 접힌다. 현재 화면이 속한 묶음은 펴진 채로 시작한다(`openGroups[key] ?? active`) —
    늘 접힌 채 시작하면 새로고침할 때마다 지금 보고 있는 화면이 메뉴에서 사라진다.
  · 검색은 갈 수 있는 항목만 평평하게 펴서 훑고, 결과에 「묶음 › 이름」 꼬리표를 단다.
  · 「오늘」 묶음의 항목에는 할 일 건수가 붙는다(`useTodoCounts`) — 위젯과 같은 쿼리, 같은 규칙.
*/

function linkClass({ isActive }: { isActive: boolean }): string {
  const base = 'flex items-center gap-2.5 rounded-md px-3 py-2.5 text-ui-md transition-colors';
  return isActive ? `${base} font-medium` : base;
}

function linkStyle({ isActive }: { isActive: boolean }) {
  return isActive
    ? { background: 'var(--accent-bg)', color: 'var(--accent)' }
    : { color: 'var(--text-secondary)' };
}

/** 사이드바 할 일 건수 배지(빨간 알약). front 의 `CountBadge` 와 같은 옷이다. */
function CountBadge({ n }: { n: number }) {
  return (
    <span
      className="ml-auto shrink-0 rounded-full text-white"
      style={{ fontSize: '10px', fontWeight: 700, background: '#dc2626', padding: '1px 6px', lineHeight: 1.5 }}
      aria-label={`할 일 ${n}건`}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <span
      className="shrink-0"
      style={{ color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
      aria-hidden
    >
      <MenuIcon name="chevron-down" size={13} />
    </span>
  );
}

/** 검색이 훑는 한 줄 — 어느 묶음에 있었는지를 함께 들고 다닌다. */
type Hit = { key: string; to: string; label: string; icon: string; group?: string; aliases: string[]; count: number };

/**
 * 좌측 메뉴.
 *
 * <p>현장이 정해져야 갈 곳이 정해진다 — 현장 항목이 전부 `/sites/:siteId/…` 를 가리키기 때문이다.
 * 그래서 현장을 못 고른 상태(배정 0)에서는 홈과 업체 묶음만 남는다.
 *
 * <h2>세로로 셋으로 나뉜다</h2>
 * 검색칸(고정) · 메뉴(스크롤) · 위젯 관리(바닥 고정). 전체를 스크롤하면 바닥의 위젯 관리가
 * 화면 밖으로 밀려난다 — front 가 같은 이유로 이렇게 나눠 두었다.
 */
export function Sidebar({
  siteId,
  partyType,
  kinds = [],
  onNavigate,
}: {
  siteId: string | undefined;
  partyType: PartyType;
  /** 업체가 맡는 갈래들(V364). 세션에서 온다. <b>비면 거르지 않는다</b>(미지정·직원·감리). */
  kinds?: VendorKind[];
  /** 모바일 서랍에서 항목을 누르면 서랍을 닫는다. */
  onNavigate?: () => void;
}) {
  const [query, setQuery] = useState('');
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const { pathname } = useLocation();
  const counts = useTodoCounts(siteId ?? '', partyType);

  /*
    화면이 바뀌면 검색어를 비운다. 이동했다는 것은 검색이 제 할 일을 다했다는 뜻이고,
    남겨 두면 다음에 서랍을 열었을 때 걸러진 목록이 나와 메뉴가 사라진 것처럼 보인다.
    링크의 onClick 이 아니라 경로를 보는 것은, 뒤로가기로 옮겼을 때도 지워야 해서다.
  */
  useEffect(() => {
    setQuery('');
  }, [pathname]);

  /*
    지금 서 있는 <b>공간</b>(사용자 지시 2026-09-30). 경로가 정한다 — 상태로 들고 있으면
    새로고침·뒤로가기·링크로 들어온 사람의 사이드바가 화면과 어긋난다.
  */
  const space = spaceOfPath(pathname);

  // 현장이 없으면 현장 항목은 갈 곳이 없다 — 묶음째 빠진다.
  // 업체의 갈래(외주·구매·용역)로 한 번 더 거른다. 세션이 아직이면(null) 안 거른다 — 잠깐 다 보이는 쪽이 낫다.
  const groups = useMemo<MenuGroup[]>(
    () =>
      menuFor(partyType, kinds, space)
        .map((group) => ({ ...group, items: group.items.filter((item) => hrefOf(item, siteId) !== null) }))
        .filter((group) => group.items.length > 0),
    [partyType, kinds, siteId, space],
  );

  /*
    검색은 **갈 수 있는 곳만** 평평하게 펴서 훑는다. 묶음 이름은 목적지가 아니라 걸리지 않는다 —
    눌러도 갈 데가 없으면 결과가 아니라 막다른 길이다. 꼬리표에는 그 묶음을 적는다.
  */
  const hits = useMemo<Hit[]>(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return [];

    const all: Hit[] = [
      // 홈은 현장 업무 공간의 것이다 — 견적 공간의 첫 화면은 공고 목록이고 위젯이 없다.
      ...(space === RFQ_SPACE
        ? []
        : [{ key: 'home', to: '/', label: '홈', icon: 'layout', aliases: ['대시보드', '위젯'], count: 0 }]),
      ...groups.flatMap((group) =>
        group.items.map((item) => ({
          key: item.key,
          to: hrefOf(item, siteId) ?? '/',
          label: item.label,
          icon: item.icon,
          group: group.label,
          aliases: item.aliases ?? [],
          count: counts[item.key] ?? 0,
        })),
      ),
    ];

    return all.filter(
      (hit) =>
        hit.label.toLowerCase().includes(needle) ||
        hit.aliases.some((alias) => alias.toLowerCase().includes(needle)),
    );
  }, [query, groups, siteId, counts, space]);

  const searching = query.trim() !== '';

  const renderItem = (item: MenuItem) => {
    const to = hrefOf(item, siteId) ?? '/';
    const count = counts[item.key] ?? 0;
    return (
      <NavLink key={item.key} to={to} className={linkClass} style={linkStyle} onClick={onNavigate}>
        <MenuIcon name={item.icon} size={16} />
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {count > 0 && <CountBadge n={count} />}
      </NavLink>
    );
  };

  const renderGroup = (group: MenuGroup) => {
    const active = group.items.some((item) => {
      const to = hrefOf(item, siteId);
      return to !== null && (pathname === to || pathname.startsWith(`${to}/`));
    });
    const open = openGroups[group.key] ?? active;
    // 접혀 있어도 할 일이 있다는 것은 보여야 한다 — 묶음 줄에 합계를 단다.
    const total = open ? 0 : group.items.reduce((sum, item) => sum + (counts[item.key] ?? 0), 0);

    return (
      <div key={group.key}>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-ui-md transition-colors"
          style={{ color: active ? 'var(--accent)' : 'var(--text-secondary)', fontWeight: active ? 600 : 400 }}
          aria-expanded={open}
          onClick={() => setOpenGroups((prev) => ({ ...prev, [group.key]: !open }))}
        >
          <span className="min-w-0 flex-1 truncate text-left">{group.label}</span>
          {total > 0 && <CountBadge n={total} />}
          <Chevron open={open} />
        </button>
        {open && <div className="flex flex-col gap-0.5 pl-2">{group.items.map(renderItem)}</div>}
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col">
      {/* 검색칸은 스크롤 밖이다 — 메뉴를 한참 내려간 뒤에도 그대로 있어야 찾으러 올라오지 않는다. */}
      <div className="shrink-0 px-3 pb-1 pt-3">
        <div className="relative">
          <span
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-muted)' }}
          >
            <MenuIcon name="search" size={14} />
          </span>
          <input
            className="input-field min-h-0 py-1.5 pl-8 text-ui-xs"
            placeholder="메뉴 검색"
            aria-label="메뉴 검색"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </div>

      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3 pt-2">
        {searching ? (
          hits.length === 0 ? (
            <p className="px-3 py-4 text-center text-ui-xs" style={{ color: 'var(--text-muted)' }}>
              검색 결과가 없습니다.
            </p>
          ) : (
            /*
              결과는 한 줄에 이름과 묶음을 함께 적는다. 「검측」처럼 이름만으로는 어느 묶음의 것인지
              모르는 것이 있어서다 — 「오늘 › 검측」.
            */
            hits.map((hit) => (
              <NavLink
                key={hit.key}
                to={hit.to}
                end={hit.to === '/'}
                className={linkClass}
                style={linkStyle}
                onClick={onNavigate}
              >
                <MenuIcon name={hit.icon} size={16} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{hit.label}</span>
                  {hit.group !== undefined && (
                    <span className="block truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                      {hit.group} › {hit.label}
                    </span>
                  )}
                </span>
                {hit.count > 0 && <CountBadge n={hit.count} />}
              </NavLink>
            ))
          )
        ) : (
          <>
            {/* 홈은 현장 업무 공간의 것이다 — 견적 공간의 첫 화면은 공고 목록이다. */}
            {space !== RFQ_SPACE && (
              <NavLink to="/" end className={linkClass} style={linkStyle} onClick={onNavigate}>
                <MenuIcon name="layout" size={16} />
                홈
              </NavLink>
            )}
            {groups.map(renderGroup)}
          </>
        )}
      </nav>

      {/*
        위젯 관리 — 바닥에 붙는다. 메뉴가 아니라 **화면 설정**이라 목록과 선을 그어 나눈다.

        누르면 홈으로 보내면서 `?widgets=1` 을 단다. 관리 결과는 곧바로 눈으로 봐야 하는데,
        다른 화면 위에 모달만 띄우면 무엇이 어떻게 바뀌었는지 확인할 수 없다. front 와 같다.
      */}
      <div className="shrink-0" style={{ borderTop: '1px solid var(--border)' }}>
        {/* 위젯은 홈의 것이라 현장 업무 공간에만 있다. */}
        {space !== RFQ_SPACE && (
          <NavLink
            to="/home?widgets=1"
            className="flex items-center gap-2.5 px-4 py-3 text-ui-sm"
            style={{ color: 'var(--text-secondary)' }}
            onClick={onNavigate}
          >
            <MenuIcon name="sliders" size={16} />
            위젯 관리
          </NavLink>
        )}

        {/*
          <b>공간을 옮기는 유일한 문</b>(사용자 지시 2026-09-30). 두 공간이 갈린 뒤로는 사이드바만
          보고 저쪽으로 갈 길이 없다 — 관문으로 돌아가 다른 칸을 누른다.

          ★ 로그아웃이 아니다. 관문은 로그인한 채로 서는 화면이고(`LoginPage`), 거기서 프로필 카드와
            문들이 보인다. 이 줄을 「로그아웃」으로 두면 공간을 옮기려는 사람이 매번 다시 로그인한다.
        */}
        <NavLink
          to="/login"
          className="flex items-center gap-2.5 px-4 py-3 text-ui-sm"
          style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}
          onClick={onNavigate}
        >
          <MenuIcon name="grid" size={16} />
          로그인 페이지
        </NavLink>
      </div>
    </div>
  );
}
